<script setup lang="ts">
// Working multi-view: up to 4 fully-interactive terminals in a resizable
// grid (§2.3). Each pane is an independent read-write attach to a running
// session — type into any of them. Cap 4 (2×2); no layout persistence — the
// selection resets when you leave the view.
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import { useMachinesStore } from "@/stores/machines";
import type { RemoteSession } from "@/api/types";
import TerminalPane from "@/components/TerminalPane.vue";

const { t } = useI18n();
const router = useRouter();
const machines = useMachinesStore();

const MAX = 4;
type Status = "connecting" | "connected" | "closed";

const selectedIds = ref<string[]>([]);
const focusedId = ref<string | null>(null);
const statuses = ref<Record<string, Status>>({});
const pick = ref("");

onMounted(() => void machines.start());
onBeforeUnmount(() => machines.stop());

const running = computed(() => machines.sessions.filter((s) => s.status === "running"));
// Resolve selected ids → live records, dropping any that stopped meanwhile.
const panes = computed<RemoteSession[]>(() =>
  selectedIds.value
    .map((id) => running.value.find((s) => s.id === id))
    .filter((s): s is RemoteSession => s !== undefined),
);
const available = computed(() => running.value.filter((s) => !selectedIds.value.includes(s.id)));
const full = computed(() => selectedIds.value.length >= MAX);

function addSession(id: string) {
  if (!id || full.value || selectedIds.value.includes(id)) return;
  selectedIds.value.push(id);
  focusedId.value = id;
  pick.value = "";
}
function removeSession(id: string) {
  selectedIds.value = selectedIds.value.filter((x) => x !== id);
  delete statuses.value[id];
  if (focusedId.value === id) focusedId.value = selectedIds.value[0] ?? null;
}
function openFull(id: string) {
  router.push({ name: "session", params: { sessionId: id } });
}
function onStatus(id: string, s: Status) {
  statuses.value = { ...statuses.value, [id]: s };
}
function label(s: RemoteSession) {
  return s.name || s.engine || s.id.slice(0, 8);
}
function tone(id: string) {
  const s = statuses.value[id];
  return s === "connected" ? "ok" : s === "closed" ? "bad" : "warn";
}

const gridClass = computed(() => `split__grid--${Math.min(panes.value.length, MAX) || 1}`);
</script>

<template>
  <section class="split">
    <header class="split__bar">
      <h1 class="split__title">{{ t("split.title") }}</h1>
      <span class="split__count">{{ panes.length }} / {{ MAX }}</span>
      <span class="split__spacer"></span>
      <select
        v-model="pick"
        class="split__add"
        :disabled="full || available.length === 0"
        @change="addSession(pick)"
      >
        <option value="">
          {{ full ? t("split.full") : available.length ? t("split.add") : t("split.none") }}
        </option>
        <option v-for="s in available" :key="s.id" :value="s.id">{{ label(s) }}</option>
      </select>
    </header>

    <div v-if="panes.length === 0" class="split__empty">{{ t("split.empty") }}</div>

    <div v-else class="split__grid" :class="gridClass">
      <div
        v-for="s in panes"
        :key="s.id"
        class="split__cell"
        :class="{ 'split__cell--active': focusedId === s.id }"
        @mousedown="focusedId = s.id"
      >
        <div class="split__head">
          <span class="split__dot" :data-tone="tone(s.id)"></span>
          <span class="split__name">{{ label(s) }}</span>
          <span class="split__spacer"></span>
          <button
            type="button"
            class="split__icon"
            :title="t('split.open')"
            :aria-label="t('split.open')"
            @click.stop="openFull(s.id)"
          >
            ↗
          </button>
          <button
            type="button"
            class="split__icon"
            :title="t('split.remove')"
            :aria-label="t('split.remove')"
            @click.stop="removeSession(s.id)"
          >
            ✕
          </button>
        </div>
        <TerminalPane
          :session-id="s.id"
          :target-type="s.target_type"
          class="split__pane"
          @status="(v) => onStatus(s.id, v)"
        />
      </div>
    </div>
  </section>
</template>

<style scoped>
.split {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  height: 100%;
}
.split__bar {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}
.split__title {
  font-size: 1.05rem;
  margin: 0;
}
.split__count {
  font-size: 0.78rem;
  color: var(--color-fg-dim);
  font-family: var(--font-mono);
}
.split__spacer {
  flex: 1;
}
.split__add {
  padding: 0.4rem 0.6rem;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  background: var(--color-bg);
  color: var(--color-fg);
  font-size: 0.85rem;
  max-width: 14rem;
}
.split__empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-fg-dim);
  border: 1px dashed var(--color-border);
  border-radius: 8px;
}
.split__grid {
  flex: 1;
  min-height: 0;
  display: grid;
  gap: 0.5rem;
}
.split__grid--1 {
  grid-template-columns: 1fr;
}
.split__grid--2 {
  grid-template-columns: 1fr 1fr;
}
.split__grid--3,
.split__grid--4 {
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
}
.split__cell {
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  overflow: hidden;
  background: var(--color-bg);
}
.split__cell--active {
  border-color: var(--color-accent);
}
.split__head {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.3rem 0.5rem;
  background: var(--color-bg-elev);
  border-bottom: 1px solid var(--color-border);
  font-size: 0.78rem;
}
.split__dot {
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 50%;
  flex: 0 0 auto;
  background: var(--color-fg-dim);
}
.split__dot[data-tone="ok"] {
  background: var(--color-ok);
}
.split__dot[data-tone="warn"] {
  background: var(--color-warn);
}
.split__dot[data-tone="bad"] {
  background: var(--color-error);
}
.split__name {
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.split__icon {
  flex: 0 0 auto;
  border: none;
  background: transparent;
  color: var(--color-fg-dim);
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0 0.2rem;
}
.split__icon:hover {
  color: var(--color-fg);
}
.split__pane {
  flex: 1;
  min-height: 0;
}

/* Narrow screens: stack panes in one column (split is a desktop workflow). */
@media (max-width: 760px) {
  .split__grid--2,
  .split__grid--3,
  .split__grid--4 {
    grid-template-columns: 1fr;
    grid-template-rows: none;
    grid-auto-rows: 18rem;
  }
}
</style>
